import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaMapMarkerAlt, FaClock, FaStar, FaCalendarCheck } from 'react-icons/fa';
import { publicGarageService } from '../../services/apiService';

const GarageSelect = () => {
  const navigate = useNavigate();
  const { garageId } = useParams();
  const location = useLocation();
  const state = location.state || {};

  const [garage, setGarage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const list = await publicGarageService.list();
        const garages = Array.isArray(list) ? list : [];
        const match = garages.find((g) => String(g._id || g.id) === String(garageId));
        if (mounted) {
          if (match) {
            const distance = state.distance != null
              ? (typeof state.distance === 'number'
                  ? `${state.distance.toFixed(1)} km`
                  : String(state.distance))
              : null;
            setGarage({
              id: match._id || match.id,
              name: match.name || 'Garage',
              address: match.location || '',
              distance: distance ?? '—',
              rating: match.rating ?? 4.5,
              reviews: match.reviews ?? 0
            });
          } else {
            setGarage(null);
            setError('Garage not found.');
          }
        }
      } catch (e) {
        if (mounted) {
          setError(e.message || 'Failed to load garage.');
          setGarage(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [garageId, state.distance]);

  const handleBookService = () => {
    navigate(`/service/booking/${garageId || garage?.id}`);
  };

  if (loading) {
    return (
      <div className="checkout-light-page min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8 flex items-center justify-center">
        <div className="text-gray-500">Loading garage…</div>
      </div>
    );
  }

  if (error || !garage) {
    return (
      <div className="checkout-light-page min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8">
        <div className="section-container max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-soft p-6 text-center">
            <p className="text-red-600 mb-4">{error || 'Garage not found.'}</p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-outline"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-light-page min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8">
      <div className="section-container max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Select Garage</h1>
          <p className="text-gray-600">Review the workshop and proceed to book your service</p>
        </div>

        <div className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              {garage.name}
            </h2>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 shrink-0">
                  <FaMapMarkerAlt className="text-lg" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Address</p>
                  <p className="text-gray-900 font-medium">{garage.address || '—'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 shrink-0">
                  <FaClock className="text-lg" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Distance</p>
                  <p className="text-gray-900 font-medium">{garage.distance}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 shrink-0">
                  <FaStar className="text-lg text-amber-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rating</p>
                  <p className="text-gray-900 font-medium">
                    {Number(garage.rating).toFixed(1)}
                    {garage.reviews ? ` (${garage.reviews} reviews)` : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 shrink-0">
                  <FaCalendarCheck className="text-lg" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Available slots</p>
                  <p className="text-gray-600 text-sm">Select date & time when you book service</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={handleBookService}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-semibold"
              >
                <FaCalendarCheck />
                Book Service
              </button>
              <p className="text-center text-sm text-gray-500 mt-3">
                You’ll choose your vehicle and service details on the next step.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GarageSelect;
